import { Injectable } from '@angular/core';
import { delayWhen, BehaviorSubject, EMPTY, Observable, retryWhen, scan, Subject, switchMap, timer, catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { ISkillSaveRequest } from '../models/skill-save-request';
import { SkillService } from './skill.service';
import { MessageConstants } from '../../shared/constants/message-constants';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Skill } from '../models/skill';
import { SkillConstants } from '../constants/skill-constants';
import { LinkedTrainingItem } from '../models/linked-item';
import { CourseCatalogueSearchResult } from '../../course-catalogue/models/course-catalogue-search-result';
import { TrainingItemGroupDetails } from '../../training-item/models/training-item-group-detail';
import { AssignmentGroupInteractionService } from '../../system-admin/assignment-group/services/assignment-group-interaction.service';
import { PollCompletionService } from '../../core/services/poll-completion.service';

@Injectable()
export class SkillPersistenceService {

  closeAndResetSkillSlider$ = new Subject<boolean>();
  closeAndResetTrainingItemSlider$ = new Subject<boolean>();
  resetSelectionAfterAdd$ = new Subject<boolean>();
  populateErrorsForTab$ = new BehaviorSubject<boolean>(false);
  errorTabIndices: number[] = [];
  currentTabInSliderMenu$ = new Subject<number>();
  currentTabInSliderMenuByCustomChange$ = new Subject<number>();
  skill$ = new BehaviorSubject<Skill>({} as Skill);
  trainingItemsList: LinkedTrainingItem[] = []
  skillDetails?: Skill;
  duplicateTrainingItemsNumber: string[] = [];
  duplicateTrainingItemNumbersWithRemoveStatus: string[] = [];

  constructor(
    private skillService: SkillService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private assignmentGroupInteractionService: AssignmentGroupInteractionService,
    private pollCompletionService: PollCompletionService
  ) {}

saveSkill = (skillPayload : ISkillSaveRequest): void => {
    this.skillService
      .saveSkill(skillPayload)
      .pipe(
        switchMap((skillDetails) => {
          const id= skillDetails.id;
          return this.skillService.getSkillByID(id).pipe(
            retryWhen(errors =>
              errors.pipe(
                scan((retryCount, error) => {
                  if (retryCount >= 4) {
                    this.messageService.add({
                      severity: SkillConstants.Severity_Err,
                      summary: SkillConstants.Skill_Created_Error_Message,
                      sticky: true
                    }); 
                     throw error;  
                  }
                  return retryCount + 1;
                }, 0),
                delayWhen(() => timer(3000))
              )
            )
          );
        }),
        catchError(() => {
          this.messageService.add({
            severity: SkillConstants.Severity_Err,
            summary: SkillConstants.Skill_Created_Error_Message,
            sticky: true
          });
          return EMPTY;
        })
      )
      .subscribe(response => {

          const skillCode = response.code;
  
          this.messageService.add({
            severity: SkillConstants.Severity_Success,
            summary: SkillConstants.Skill_Created_Success_Message + skillCode,
            sticky: true
          }); 
            setTimeout(() => {
              this.openSkillDetails(skillCode);
            }, 1500);
      });
  }

  UpdateSkillService = (skillPayload: ISkillSaveRequest): void => {
    this.skillService
      .updateSkill(skillPayload)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: SkillConstants.Severity_Success,
            summary: SkillConstants.Skill_Updated_Success_Message,
            sticky: true
          });
          this.closeAndResetSkillSlider$.next(true);
        },
        error: () => {
          this.messageService.add({
            severity: SkillConstants.Severity_Err,
            summary: SkillConstants.Skill_Updated_Error_Message,
            sticky: true
          });
        }
      });
  }

  openSkillDetails(skillCode: string): void {
    this.closeAndResetSkillSlider$.next(true);
    setTimeout(() => {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/eqms.skillDetails/' + skillCode])
      );
      window.open(url, '_blank');
    }, 1000);
  }

  getScopes(): Observable<any> {
    return this.skillService.getScopes();
  }

  openConfirmationDialog = () => {
    this.confirmationService.confirm({
    message: MessageConstants.Slider_Confirmation_Text,
    header: MessageConstants.Slider_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.closeAndResetTrainingItemSlider$.next(true);
      },
    });
  }

  onTabChange = (tabIndex: number) => {
  this.currentTabInSliderMenu$.next(tabIndex);
  }
  onCustomTabChange = (tabIndex: number) => {
    this.currentTabInSliderMenuByCustomChange$.next(tabIndex);
  }

  updateTabErrorIndex = (tabIndex: number, isError: boolean) => {
    isError ? this.addErrorTabIndex(tabIndex) : this.removeErrorTabIndex(tabIndex);
    this.populateErrorsForTab$.next(true);
  }

  // To Add Error Index On Tab Changes By Tab Menu Or Using Next/Previous Button
  addErrorTabIndex = (tabIndex: number) => {
    if (this.errorTabIndices.indexOf(tabIndex) === -1) {
      this.errorTabIndices.push(tabIndex);
    }
  }

  // To Remove Error Index On Tab Changes By Tab Menu Or Using Next/Previous Button
  removeErrorTabIndex = (tabIndex: number) => {
    if (this.errorTabIndices.indexOf(tabIndex) > -1) {
      this.errorTabIndices.splice(this.errorTabIndices.indexOf(tabIndex), 1);
    }
  }

  updateSkillDetails = (skill: Skill) => {
    this.skill$.next(skill);
  }

  prepareDataForDocumentTrainingItem(documents: any[], isUpdate: boolean, skill : Skill) {
    this.trainingItemsList = [];
    this.duplicateTrainingItemNumbersWithRemoveStatus = [];
    if (isUpdate) {
      this.populateTrainingItemsFromSkill();
    }
    documents.forEach(doc => {
      const existingItem = this.trainingItemsList.find(item => item.trainingItemId === doc.number);
      if (existingItem) {
        if (existingItem.status === SkillConstants.Skill_StatusRemoved) {
          existingItem.status = SkillConstants.Skill_StatusActive;
          this.duplicateTrainingItemNumbersWithRemoveStatus.push(doc.number);
        }
      } else {
        const trainingItem: LinkedTrainingItem = this.createSkillTrainingItem(doc.number, SkillConstants.Training_Item_Type_Document, null);
        this.trainingItemsList.push(trainingItem);
      }
    });
    skill.linkedTrainingItems = this.trainingItemsList;
    this.skillDetails = skill;
  }

   pendingAddAndClose: boolean = false;

   updateSkill(addAndClose: boolean) {
      if (!this.skillDetails) {
        return;
      }
      this.pendingAddAndClose = addAndClose;
      this.skillService.updateLinkedTrainingItems(this.skillDetails)
        .pipe(
          catchError((err) => {
            this.messageService.add({
              severity: SkillConstants.Severity_Err,
              summary: `${err.error}`,
            });
            return throwError(() => err);
          })
        ).subscribe({
          next: (response) => {
            this.pollCompletionService.emit({
              traceId: response?.traceId ?? '',
              domain: SkillConstants.PollDomain_LinkTrainingItems,
              data: response,
              status: 'completed'
            });
            if (this.pendingAddAndClose) {
              this.closeAndResetTrainingItemSlider$.next(true);
              this.pendingAddAndClose = false;
            } else {
              this.resetSelectionAfterAdd$.next(true);
            }
          },
          error: () => { /* Error already handled in catchError */ }
        });
    }
  
    prepareDataForCourseTrainingItem(courses: CourseCatalogueSearchResult[], isUpdate: boolean, skill?: Skill) {
      this.trainingItemsList = [];
      this.duplicateTrainingItemNumbersWithRemoveStatus = [];
      if (isUpdate) {
        this.populateTrainingItemsFromSkill();
      }
      courses.forEach(course => {
        const existingItem = this.trainingItemsList.find(item => item.trainingItemId === course.courseNumber);
        if (existingItem) {
          if (existingItem.status === SkillConstants.Skill_StatusRemoved) {
            existingItem.status = SkillConstants.Skill_StatusActive;
            this.duplicateTrainingItemNumbersWithRemoveStatus.push(course.courseNumber);
          }
        } else {
          const trainingItem: LinkedTrainingItem = this.createSkillTrainingItem(course.courseNumber, SkillConstants.Training_Item_Type_Course, null);
          this.trainingItemsList.push(trainingItem);
        }
      });
      skill!.linkedTrainingItems = this.trainingItemsList;
      this.skillDetails = skill;
    }
  
    prepareDataForTrainingItemGroupTrainingItem(trainingItemGroups: TrainingItemGroupDetails[], isUpdate: boolean, skill?: Skill) {
      this.trainingItemsList = [];
      this.duplicateTrainingItemsNumber = [];
      this.duplicateTrainingItemNumbersWithRemoveStatus = [];
      if (isUpdate) {
        this.populateTrainingItemsFromSkill();
      }
      trainingItemGroups.forEach((trainingItemGroup) => {
        trainingItemGroup.trainingItems.forEach((trainingItem) => {
          const resolvedType = this.resolveTrainingItemType(trainingItem);
          if (this.trainingItemsList.length > 0) {
            const existingItem = this.trainingItemsList
              .find(item => item.trainingItemId === trainingItem.trainingItemNumber);
              
            if (existingItem) {
              const wasRemoved = existingItem.status === SkillConstants.Skill_StatusRemoved;
              if (wasRemoved) {
                this.duplicateTrainingItemNumbersWithRemoveStatus.push(trainingItem.trainingItemNumber);
              } else {
                this.duplicateTrainingItemsNumber.push(trainingItem.trainingItemNumber);
              }
  
              if (wasRemoved) {
                  existingItem.trainingItemGroups = [trainingItemGroup.number];
                  existingItem.status = SkillConstants.Skill_StatusActive;
                  return;
              }
  
              if (!Array.isArray(existingItem.trainingItemGroups)) {
                  existingItem.trainingItemGroups = [trainingItemGroup.number];
                  return;
              }

              const hasGroups = Array.isArray(existingItem.trainingItemGroups) && existingItem.trainingItemGroups.length > 0;
              const isTrainingItemGroupExists = hasGroups && existingItem.trainingItemGroups!
                .includes(trainingItemGroup.number);
              if(!isTrainingItemGroupExists) {
                if (hasGroups) {
                  existingItem.trainingItemGroups!.push(trainingItemGroup.number);
                } else {
                  existingItem.trainingItemGroups = [trainingItemGroup.number];
                }
              }
              
            } else {
              this.addTrainingItem(trainingItem.trainingItemNumber, resolvedType, trainingItemGroup.number, SkillConstants.Skill_StatusActive);
            }

          }
          else{
            this.addTrainingItem(trainingItem.trainingItemNumber, resolvedType, trainingItemGroup.number, SkillConstants.Skill_StatusActive);
          }
        })
      });
      if (skill) {
        skill.linkedTrainingItems = this.trainingItemsList;
      }
      this.mapSkillDto(skill!);
    }
  
  
    private createSkillTrainingItem(id: string, type: number, trainingItemGroupNumber: string | null, status : number = SkillConstants.Skill_StatusActive): LinkedTrainingItem {
      let skillTrainingItem: LinkedTrainingItem = {
        trainingItemId: id,
        trainingItemType: type,
        status: status,
        trainingItemGroups: trainingItemGroupNumber !== null ? [trainingItemGroupNumber] : null,
      };
      return skillTrainingItem;
    }
  
    private populateTrainingItemsFromSkill = () => {
      this.skill$.value?.linkedTrainingItems?.forEach(training => {
        const item: LinkedTrainingItem = {
          trainingItemId: training.trainingItemId,
          trainingItemType: (training as any).type || (training as any).trainingItemType || 0,
          status: training.status,
          trainingItemGroups: training.trainingItemGroups ?? null,
        };
        this.trainingItemsList.push(item);
      });
    }
  
    private addTrainingItem(trainingItemNumber: string, type: number, trainingItemGroupNumber: string | null, status: number = SkillConstants.Skill_StatusActive): void {
      const trainingItem = this.createSkillTrainingItem(trainingItemNumber, type, trainingItemGroupNumber, status);
      this.trainingItemsList.push(trainingItem);
    }

    /**
     * Resolves the training item type from both possible API field names.
     * The API may return 'trainingType' or 'trainingItemType' depending on the endpoint.
     */
    private resolveTrainingItemType(trainingItem: any): number {
      return trainingItem.trainingItemType || trainingItem.trainingType || 0;
    }

    private mapSkillDto(skill : Skill){
      this.skillDetails = {
        code: skill.code,
        name: skill.name,
        description: skill.description,
        eQMSScopes: skill.eQMSScopes,
        linkedTrainingItems: skill.linkedTrainingItems,
        validityPeriod: skill.validityPeriod,
        trainingCriteria: skill.trainingCriteria,
        verificationRequired: skill.verificationRequired,
        acknowledgementRequired: skill.acknowledgementRequired,
        isActive: skill.isActive
      }
    }
}
 
